import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { loadCalendarMonth, calculateWorkingDays, addWorkingDays } from '../../utils/productionCalendar';
import { parseLocalDateTime, formatLocalDateTime } from '../../utils/dateTime';
import './CustomDateTimePicker.css';

let calendarOpenCount = 0;

const toggleCalendarOpenFlag = (isOpen) => {
  try {
    if (isOpen) {
      calendarOpenCount += 1;
      document.body.classList.add('aot-calendar-open');
    } else {
      calendarOpenCount = Math.max(0, calendarOpenCount - 1);
      if (calendarOpenCount === 0) {
        document.body.classList.remove('aot-calendar-open');
      }
    }
  } catch (_) {
    // ignore
  }
};

const CustomDateTimePicker = ({
  value,
  onChange,
  label,
  required = false,
  min,
  max,
  baseDate = null, // Базовая дата для расчета периода
  baseLabel = "От даты",
  disabled = false,
  calculatorPosition = 'below', // above | below
  showFlowArrows = false,
  compactCalculator = false,
  topFlowArrowClassName = '',
  bottomFlowArrowClassName = '',
  highlightDates = [],
  popupPosition = 'below', // below | above
  showLabel = true,
  inputClassName = '',
  inlineCalendarSize = 'sm',
  defaultDayType = 'calendar',
  onInputFocus,
  onInputBlur
}) => {
  const initialParsedDate = value ? parseLocalDateTime(value) : null;
  const [showCalendar, setShowCalendar] = useState(false);
  const [effectivePopupPosition, setEffectivePopupPosition] = useState(popupPosition);
  const [popupInlineStyle, setPopupInlineStyle] = useState({});
  const [displayDate, setDisplayDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(initialParsedDate);
  const [selectedTime, setSelectedTime] = useState(() => (
    initialParsedDate
      ? `${String(initialParsedDate.getHours()).padStart(2, '0')}:${String(initialParsedDate.getMinutes()).padStart(2, '0')}`
      : '12:00'
  ));
  const [inputValue, setInputValue] = useState(() => (
    initialParsedDate
      ? formatLocalDateTime(initialParsedDate).replace(',', '').trim()
      : ''
  ));
  const [dateOnlyInput, setDateOnlyInput] = useState(false);
  const isTypingRef = useRef(false);
  const [calendarData, setCalendarData] = useState({});
  const calendarRef = useRef(null);
  const popupRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const lastMonthScrollAtRef = useRef(0);
  
  // Состояние для калькулятора периодов (если baseDate задана)
  const [period, setPeriod] = useState({
    days: 0,
    workingDays: 0,
    weeks: 0,
    months: 0,
    years: 0
  });
  const [periodType, setPeriodType] = useState('days');
  const [periodValue, setPeriodValue] = useState('');
  const [dayType, setDayType] = useState(defaultDayType);

  const highlightSet = React.useMemo(() => {
    const set = new Set();
    (highlightDates || []).forEach((item) => {
      const d = item instanceof Date ? item : new Date(item);
      if (!d || Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      set.add(key);
    });
    return set;
  }, [highlightDates]);

  // Обновление времени при изменении value
  useEffect(() => {
    if (isTypingRef.current) {
      return;
    }
    if (value) {
      const date = parseLocalDateTime(value);
      if (!date) {
        setInputValue('');
        return;
      }
      setSelectedDate(date);
      setDisplayDate(new Date(date.getFullYear(), date.getMonth(), 1));
      setSelectedTime(
        String(date.getHours()).padStart(2, '0') + ':' + 
        String(date.getMinutes()).padStart(2, '0')
      );
      if (dateOnlyInput) {
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = String(date.getFullYear());
        setInputValue(`${dd}.${mm}.${yyyy}`);
      } else {
        setInputValue(formatLocalDateTime(date).replace(',', '').trim());
      }
    } else {
      setInputValue('');
      setDateOnlyInput(false);
    }
  }, [value, dateOnlyInput]);

  // Вычисление периода от базовой даты (если есть)
  const calculatePeriod = async (start, end) => {
    if (!start || !end) return { days: 0, workingDays: 0, weeks: 0, months: 0, years: 0 };
    
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (endDate < startDate) return { days: 0, workingDays: 0, weeks: 0, months: 0, years: 0 };
    
    const diffTime = Math.abs(endDate - startDate);
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Вычисляем рабочие дни
    let workingDays = 0;
    try {
      workingDays = await calculateWorkingDays(startDate, endDate);
    } catch (error) {
      console.warn('Ошибка подсчета рабочих дней:', error);
      workingDays = Math.floor(days * 5 / 7);
    }
    
    // Вычисляем месяцы и годы
    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
    months -= startDate.getMonth();
    months += endDate.getMonth();
    
    const years = Math.floor(months / 12);
    months = months % 12;
    
    if (endDate.getDate() < startDate.getDate()) {
      months--;
      if (months < 0) {
        months = 11;
      }
    }
    
    return {
      days: days,
      workingDays: workingDays,
      weeks: Math.floor(days / 7),
      months: months,
      years: years
    };
  };

  // Обновление периода при изменении baseDate или value
  useEffect(() => {
    if (baseDate && value) {
      const updatePeriod = async () => {
        const newPeriod = await calculatePeriod(baseDate, value);
        setPeriod(newPeriod);
      };
      updatePeriod();
    }
  }, [baseDate, value]);

  // Расчет даты на основе периода
  const calculateEndDate = async (start, value, type, useWorkingDays = false) => {
    if (!start || !value) return '';
    
    const startDate = new Date(start);
    let endDate = new Date(start);
    
    switch(type) {
      case 'days':
        if (useWorkingDays) {
          try {
            endDate = await addWorkingDays(startDate, parseInt(value));
            if (!endDate) {
              endDate = new Date(start);
              endDate.setDate(startDate.getDate() + parseInt(value));
            }
          } catch (error) {
            console.warn('Ошибка расчета рабочих дней:', error);
            endDate.setDate(startDate.getDate() + parseInt(value));
          }
        } else {
          endDate.setDate(startDate.getDate() + parseInt(value));
        }
        break;
      case 'weeks':
        endDate.setDate(startDate.getDate() + (parseInt(value) * 7));
        break;
      case 'months':
        endDate.setMonth(startDate.getMonth() + parseInt(value));
        break;
      case 'years':
        endDate.setFullYear(startDate.getFullYear() + parseInt(value));
        break;
      default:
        break;
    }
    
    // Добавляем время
    const [hours, minutes] = selectedTime.split(':');
    endDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    
    const pad = (v) => String(v).padStart(2, '0');
    return `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`;
  };

  // Обработка изменения периода
  const handlePeriodValueChange = async (value) => {
    setPeriodValue(value);
    if (baseDate && value) {
      const newDate = await calculateEndDate(baseDate, value, periodType, dayType === 'working');
      if (newDate) {
        onChange({ target: { value: newDate } });
      }
    }
  };

  // Загрузка данных календаря
  useEffect(() => {
    const loadData = async () => {
      const year = displayDate.getFullYear();
      const month = displayDate.getMonth();
      try {
        const data = await loadCalendarMonth(year, month);
        const monthKey = `${year}-${month}`;
        setCalendarData(prev => ({ ...prev, [monthKey]: data }));
      } catch (error) {
        console.error('Error loading calendar data:', error);
      }
    };
    loadData();
  }, [displayDate]);

  // Закрытие календаря при клике вне его или нажатии Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    
  const handleEscape = (event) => {
      if (event.key === 'Escape' && showCalendar) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        setShowCalendar(false);
        return;
      }
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && showCalendar) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        navigateMonth(event.key === 'ArrowRight' ? 1 : -1);
      }
    };

    const handleForceClose = () => {
      if (showCalendar) {
        setShowCalendar(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('aot-close-calendar', handleForceClose);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, true);
      document.removeEventListener('aot-close-calendar', handleForceClose);
    };
  }, [showCalendar]);

  useEffect(() => {
    if (showCalendar) {
      toggleCalendarOpenFlag(true);
      return () => toggleCalendarOpenFlag(false);
    }
    return undefined;
  }, [showCalendar]);

  // Позиционируем календарь до отрисовки кадра, чтобы не было "скачка".
  useLayoutEffect(() => {
    if (!showCalendar) return undefined;
    const rafId = requestAnimationFrame(() => {
      const popupEl = popupRef.current;
      const toggleEl = toggleBtnRef.current;
      const anchorEl = inputWrapperRef.current || toggleEl;
      if (!popupEl || !anchorEl) return;
      const margin = 12;
      const popupRect = popupEl.getBoundingClientRect();
      const anchorRect = anchorEl.getBoundingClientRect();

      if (popupPosition !== 'center') {
        const spaceBelow = window.innerHeight - anchorRect.bottom - margin;
        const spaceAbove = anchorRect.top - margin;
        const popupHeight = popupRect.height;
        const canFullyOpenAbove = spaceAbove >= popupHeight;
        const shouldOpenAbove = spaceBelow < popupHeight && canFullyOpenAbove;
        const nextPos = shouldOpenAbove ? 'above' : 'below';
        if (nextPos !== effectivePopupPosition) {
          setEffectivePopupPosition(nextPos);
          return;
        }
      }

      // Фиксируем попап относительно кнопки поля, чтобы он не "уезжал" из-за вложенных контейнеров.
      if (popupPosition !== 'center') {
        const width = popupRect.width || 280;
        const height = popupRect.height || 420;
        let left = anchorRect.left;
        left = Math.min(left, window.innerWidth - width - margin);
        left = Math.max(margin, left);

        let top = effectivePopupPosition === 'above'
          ? (anchorRect.top - height - 8)
          : (anchorRect.bottom + 8);

        if (top < margin) top = margin;
        if (top + height > window.innerHeight - margin) {
          top = Math.max(margin, window.innerHeight - height - margin);
        }

        const nextStyle = {
          position: 'fixed',
          top: `${Math.round(top)}px`,
          left: `${Math.round(left)}px`,
          marginTop: 0,
          marginBottom: 0
        };
        setPopupInlineStyle((prev) => {
          if (
            prev.position === nextStyle.position &&
            prev.top === nextStyle.top &&
            prev.left === nextStyle.left &&
            prev.marginTop === nextStyle.marginTop &&
            prev.marginBottom === nextStyle.marginBottom
          ) {
            return prev;
          }
          return nextStyle;
        });
      }

      // Для fixed-позиционирования дополнительно не прокручиваем страницу.
      // Координаты уже зажаты в viewport.
    });
    return () => cancelAnimationFrame(rafId);
  }, [showCalendar, displayDate, popupPosition, effectivePopupPosition]);

  useEffect(() => {
    if (!showCalendar) {
      setEffectivePopupPosition(popupPosition);
      setPopupInlineStyle({});
    }
  }, [showCalendar, popupPosition]);

  // Навигация по месяцам
  const navigateMonth = (direction) => {
    setDisplayDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleCalendarWheel = (event) => {
    if (!showCalendar) return;
    const dx = Number(event.deltaX || 0);
    const dy = Number(event.deltaY || 0);
    const horizontalIntent = Math.abs(dx) > Math.abs(dy) ? dx : 0;
    if (horizontalIntent === 0 || Math.abs(horizontalIntent) < 12) return;

    event.preventDefault();
    event.stopPropagation();

    const now = Date.now();
    if (now - lastMonthScrollAtRef.current < 180) return;
    lastMonthScrollAtRef.current = now;

    navigateMonth(horizontalIntent > 0 ? 1 : -1);
  };

  // Получение дней месяца
  const getDaysInMonth = () => {
    const year = displayDate.getFullYear();
    const month = displayDate.getMonth();
    const monthKey = `${year}-${month}`;
    const monthData = calendarData[monthKey] || [];
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startWeekDay = (firstDay.getDay() + 6) % 7; // Понедельник = 0
    
    const days = [];
    
    // Пустые ячейки в начале
    for (let i = 0; i < startWeekDay; i++) {
      days.push(null);
    }
    
    // Дни месяца
    for (let day = 1; day <= daysInMonth; day++) {
      const dayData = monthData[day - 1];
      const date = new Date(year, month, day);
      const isWeekend = dayData === 1 || (dayData === undefined && (date.getDay() === 0 || date.getDay() === 6));
      const isSelected = selectedDate && 
        date.getFullYear() === selectedDate.getFullYear() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getDate() === selectedDate.getDate();
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isHighlighted = highlightSet.has(dateKey);
      
      // Разрешаем выбор той же даты, что и в min/max
      const minDate = min ? new Date(min) : null;
      const maxDate = max ? new Date(max) : null;
      
      // Сравниваем только даты без времени (создаем копии для сравнения)
      const dateForComparison = new Date(date);
      const isBeforeMin = minDate && dateForComparison.setHours(0,0,0,0) < new Date(minDate).setHours(0,0,0,0);
      const isAfterMax = maxDate && dateForComparison.setHours(0,0,0,0) > new Date(maxDate).setHours(0,0,0,0);
      
      const isDisabled = isBeforeMin || isAfterMax;
      
      days.push({
        day,
        date,
        isWeekend,
        isSelected,
        isToday: isToday(year, month, day),
        isDisabled,
        isHighlighted
      });
    }
    
    return days;
  };

  const isToday = (year, month, day) => {
    const today = new Date();
    return today.getFullYear() === year && 
           today.getMonth() === month && 
           today.getDate() === day;
  };

  // Выбор даты
  const selectDate = (dayInfo) => {
    if (!dayInfo || !dayInfo.date || dayInfo.isDisabled) return;
    
    const newDate = new Date(dayInfo.date);
    const [hours, minutes] = selectedTime.split(':');
    newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    
    setSelectedDate(newDate);
    setDisplayDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
  };

  // Изменение времени
  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setSelectedTime(newTime);
  };

  // Изменение даты через текстовое поле
  const handleDateInputChange = (e) => {
    isTypingRef.current = true;
    const prevValue = inputValue;
    const raw = e.target.value || '';
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      setInputValue('');
      setDateOnlyInput(false);
      onChange({ target: { value: '' } });
      return;
    }

    const dateDigits = digits.slice(0, 8);
    const timeDigits = digits.slice(8, 12);
    if (dateDigits.length >= 2) {
      const dayNum = Number(dateDigits.slice(0, 2));
      if (Number.isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
        setInputValue(prevValue);
        return;
      }
    }
    if (dateDigits.length >= 4) {
      const monthNum = Number(dateDigits.slice(2, 4));
      if (Number.isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
        setInputValue(prevValue);
        return;
      }
    }
    if (timeDigits.length >= 2) {
      const hourNum = Number(timeDigits.slice(0, 2));
      if (Number.isNaN(hourNum) || hourNum > 23) {
        setInputValue(prevValue);
        return;
      }
    }
    if (timeDigits.length === 4) {
      const minuteNum = Number(timeDigits.slice(2, 4));
      if (Number.isNaN(minuteNum) || minuteNum > 59) {
        setInputValue(prevValue);
        return;
      }
    }
    let formatted = '';
    if (dateDigits.length <= 2) {
      formatted = dateDigits;
    } else if (dateDigits.length <= 4) {
      formatted = `${dateDigits.slice(0, 2)}.${dateDigits.slice(2)}`;
    } else {
      formatted = `${dateDigits.slice(0, 2)}.${dateDigits.slice(2, 4)}.${dateDigits.slice(4)}`;
    }
    if (timeDigits.length) {
      if (timeDigits.length <= 2) {
        formatted = `${formatted} ${timeDigits}`;
      } else {
        formatted = `${formatted} ${timeDigits.slice(0, 2)}:${timeDigits.slice(2)}`;
      }
    }
    setInputValue(formatted);

    if (dateDigits.length !== 8) {
      return;
    }

    const day = dateDigits.slice(0, 2);
    const month = dateDigits.slice(2, 4);
    const year = dateDigits.slice(4, 8);
    let hours = selectedTime.split(':')[0] || '00';
    let minutes = selectedTime.split(':')[1] || '00';
    if (timeDigits.length === 4) {
      hours = timeDigits.slice(0, 2);
      minutes = timeDigits.slice(2, 4);
      setDateOnlyInput(false);
      const h = Number(hours);
      const m = Number(minutes);
      if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) {
        setInputValue(prevValue);
        return;
      }
    } else if (timeDigits.length === 2) {
      // Пользователь ввел только часы — не дописываем минуты автоматически
      hours = timeDigits;
      minutes = selectedTime.split(':')[1] || '00';
      setDateOnlyInput(false);
      return;
    } else if (timeDigits.length && timeDigits.length !== 4) {
      return;
    } else {
      setDateOnlyInput(true);
    }

    const rawDate = `${day}.${month}.${year} ${hours}:${minutes}`;
    const date = parseLocalDateTime(rawDate);
    if (!date) {
      setInputValue(prevValue);
      return;
    }
    if (Number.isNaN(date.getTime())) {
      setInputValue(prevValue);
      return;
    }
    if (
      date.getFullYear() !== Number(year) ||
      date.getMonth() + 1 !== Number(month) ||
      date.getDate() !== Number(day) ||
      date.getHours() !== Number(hours) ||
      date.getMinutes() !== Number(minutes)
    ) {
      setInputValue(prevValue);
      return;
    }
    setSelectedDate(date);
    setDisplayDate(new Date(date.getFullYear(), date.getMonth(), 1));
    if (timeDigits.length) {
      setSelectedTime(`${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`);
    }
    const pad = (v) => String(v).padStart(2, '0');
    const normalized = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    onChange({ target: { value: normalized } });
  };

  const applyCalendarSelection = () => {
    if (!selectedDate) return;
    isTypingRef.current = false;
    setDateOnlyInput(false);
    const newDate = new Date(selectedDate);
    const [hours, minutes] = selectedTime.split(':');
    newDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));

    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    const day = String(newDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}T${selectedTime}`;
    onChange({ target: { value: formattedDate } });
    setShowCalendar(false);
  };

  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  const days = getDaysInMonth();

  const renderCalculator = () => {
    if (!baseDate) return null;
    return (
      <div className={`period-flow ${showFlowArrows ? 'period-flow--arrows' : ''}`}>
        {showFlowArrows && <div className={`period-flow-arrow ${topFlowArrowClassName || ''}`.trim()} aria-hidden="true">↓</div>}
        <div className={`period-calculator ${compactCalculator ? 'period-calculator--compact' : ''}`}>
          <div className="period-display">
            <div className="period-info">
              <span className="period-label">Период {baseLabel}:</span>
              <div className="period-values">
                {period.years > 0 && <span className="period-item">{period.years} г.</span>}
                {period.months > 0 && <span className="period-item">{period.months} мес.</span>}
                {period.weeks > 0 && <span className="period-item">{period.weeks} нед.</span>}
                {period.days % 7 > 0 && <span className="period-item">{period.days % 7} дн.</span>}
              </div>
            </div>
            
            <div className="working-days-toggle">
              <button
                type="button"
                className={`toggle-btn ${dayType === 'calendar' ? 'active' : ''}`}
                onClick={() => setDayType('calendar')}
                disabled={disabled}
              >
                📅 {period.days} дн.
              </button>
              <button
                type="button"
                className={`toggle-btn ${dayType === 'working' ? 'active' : ''}`}
                onClick={() => setDayType('working')}
                disabled={disabled}
              >
                💼 {period.workingDays} раб.дн.
              </button>
            </div>
          </div>

          <div className="period-calculator-controls">
            <div className="calculator-row">
              <span className="calculator-label">Рассчитать:</span>
              <input
                type="number"
                value={periodValue}
                onChange={(e) => handlePeriodValueChange(e.target.value)}
                placeholder="Количество"
                className="period-input"
                min="1"
                disabled={disabled}
              />
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="period-type-select"
                disabled={disabled}
              >
                <option value="days">дней</option>
                <option value="weeks">недель</option>
                <option value="months">месяцев</option>
                <option value="years">лет</option>
              </select>
              <span className="calculator-suffix">{baseLabel}</span>
            </div>
          </div>
        </div>
        {showFlowArrows && <div className={`period-flow-arrow ${bottomFlowArrowClassName || ''}`.trim()} aria-hidden="true">↓</div>}
      </div>
    );
  };

  return (
    <div
      className={`custom-datetime-picker ${showLabel ? '' : `custom-datetime-picker--inline custom-datetime-picker--inline-${inlineCalendarSize}`}`.trim()}
      ref={calendarRef}
    >
      {calculatorPosition === 'above' && renderCalculator()}
      <div className="form-group">
        {showLabel && <label>{label}:</label>}
        <div className="datetime-input-wrapper" ref={inputWrapperRef}>
          <input
            type="text"
            value={inputValue}
            onChange={handleDateInputChange}
            required={required}
            className={`datetime-input ${inputClassName || ''}`.trim()}
            disabled={disabled}
            placeholder="ДД.ММ.ГГГГ ЧЧ:ММ"
            inputMode="numeric"
            onFocus={(e) => {
              isTypingRef.current = true;
              if (typeof onInputFocus === 'function') onInputFocus(e);
            }}
            onBlur={(e) => {
              isTypingRef.current = false;
              if (typeof onInputBlur === 'function') onInputBlur(e);
            }}
          />
          <button
            ref={toggleBtnRef}
            type="button"
            className="calendar-toggle"
            onClick={disabled ? undefined : () => setShowCalendar(!showCalendar)}
            disabled={disabled}
            aria-label="Открыть календарь"
          >
            <span className="calendar-icon">📅</span>
          </button>
        </div>
        {showCalendar && (
          <div
            ref={popupRef}
            className={`calendar-popup ${effectivePopupPosition === 'center' ? 'calendar-popup--center' : ''}`}
            style={popupInlineStyle}
            onWheel={handleCalendarWheel}
          >
            <div className="calendar-header">
              <button
                type="button"
                className="nav-btn"
                onClick={() => navigateMonth(-1)}
              >
                ←
              </button>
              <div className="month-year">
                {monthNames[displayDate.getMonth()]} {displayDate.getFullYear()}
              </div>
              <button
                type="button"
                className="nav-btn"
                onClick={() => navigateMonth(1)}
              >
                →
              </button>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowCalendar(false)}
              >
                ×
              </button>
            </div>

            <div className="calendar-grid">
              {weekDays.map(day => (
                <div key={day} className="weekday-header">
                  {day}
                </div>
              ))}

              {days.map((dayInfo, index) => (
                <div
                  key={index}
                  className={`calendar-day ${
                    dayInfo ? `
                      ${dayInfo.isWeekend ? 'weekend' : 'weekday'}
                      ${dayInfo.isToday ? 'today' : ''}
                      ${dayInfo.isSelected ? 'selected' : ''}
                      ${dayInfo.isDisabled ? 'disabled' : ''}
                      ${dayInfo.isHighlighted ? 'highlighted' : ''}
                    ` : ''
                  }`}
                  onClick={() => dayInfo && !dayInfo.isDisabled && selectDate(dayInfo)}
                >
                  {dayInfo?.day || ''}
                </div>
              ))}
            </div>

            <div className="time-selector">
              <label>Время:</label>
              <input
                type="time"
                value={selectedTime}
                onChange={handleTimeChange}
                className="time-input"
                disabled={disabled}
              />
              <button
                type="button"
                className="calendar-apply"
                onClick={applyCalendarSelection}
                disabled={disabled || !selectedDate}
              >
                Применить
              </button>
            </div>

            <div className="calendar-legend">
              <div className="legend-item">
                <span className="legend-color weekday"></span>
                <span>Рабочий день</span>
              </div>
              <div className="legend-item">
                <span className="legend-color weekend"></span>
                <span>Выходной/праздник</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {calculatorPosition !== 'above' && renderCalculator()}
    </div>
  );
};

export default CustomDateTimePicker;
